import {
  CHARACTER_CANVAS,
  CROWN_AT_LEVEL,
  upgradeColor,
  upgradeStarCount,
  type VisibleSlot,
  VISIBLE_SLOTS,
} from '@shefira/shared';
import type Phaser from 'phaser';

import { BLAZE_RAMP, hash } from './blaze.js';

/**
 * What an upgraded outfit looks like (ADR 0013, amended): good gear makes a
 * character flashy. Stars twinkle on every piece from +3, landing on its own
 * pixels. The halo is the whole outfit's: from an average of +3 over the nine
 * equipment slots (`auraLevel`, from the server) it lights up behind the
 * character in that level's colour, a pixel wider at every level. At +10 it
 * burns like a legendary piece's outline (`blaze.ts`): light runs round the
 * ring along the fire ramp, flames lick up from its top, embers fly off, and
 * the golden ring generated once turns over it.
 *
 * Everything is drawn on whole pixels: the halo is a pixel ring rendered for
 * its exact size rather than a sprite scaled by a fraction, and the stars are
 * three hand-set shapes, never scaled either.
 */

/** Ring radius at +3; one pixel more per level above. */
const HALO_BASE_RADIUS = 12;
/** Body centre on the 32 px canvas, where the halo is centred. */
const HALO_CENTRE_Y = -CHARACTER_CANVAS / 2;
/**
 * How hard the halo works at a level: the higher, the faster and deeper it
 * breathes, the more sparks run round its ring and the faster, and from +8 a
 * wave rolls out of it every few seconds. Tuned by eye on the frost set.
 */
interface HaloIntensity {
  /** One full breath, out and back. */
  pulseMs: number;
  /** The dimmest the ring gets during a breath. */
  pulseLow: number;
  /** Sparks running round the ring, and one lap. */
  sparks: number;
  lapMs: number;
  /** Ticks between two waves; 0 for none. */
  waveEvery: number;
}

function intensityOf(level: number): HaloIntensity {
  const t = Math.min(1, Math.max(0, (level - 3) / 7));
  return {
    pulseMs: Math.round(1800 - 1200 * t),
    pulseLow: 0.75 - 0.45 * t,
    sparks: Math.max(0, Math.min(6, level - 4)),
    lapMs: Math.round(4200 - 2800 * t),
    waveEvery: level >= 10 ? 12 : level === 9 ? 18 : level === 8 ? 26 : 0,
  };
}

/**
 * The burning halo of +10: bright arcs chasing each other round the ring and
 * one lap in ticks, how often a pixel of its top throws a flame and the
 * tallest tongue, and the chance a tick lets an ember go.
 */
const BLAZE_ARCS = 3;
const BLAZE_LAP = 22;
const FLAME_CHANCE = 0.45;
const FLAME_HEIGHT = 5;
const EMBER_CHANCE = 0.6;

type Pixel = readonly [number, number];

interface Ember {
  x: number;
  y: number;
  age: number;
  life: number;
  color: number;
  sway: number;
}

/** Whole-pixel offsets from the halo's centre at this distance from it, give or take half a pixel. */
function circlePixels(radius: number): Pixel[] {
  const pixels: Pixel[] = [];
  for (let y = -radius - 1; y <= radius + 1; y += 1) {
    for (let x = -radius - 1; x <= radius + 1; x += 1) {
      if (Math.abs(Math.hypot(x, y) - radius) < 0.5) pixels.push([x, y]);
    }
  }
  return pixels;
}

/** A wave grows this many pixels past the ring, one per tick, fading out. */
const WAVE_STEPS = 5;
/** Angle between a spark's head and each dot of its tail. */
const SPARK_TAIL_STEP = 0.14;
/** One step of a star's twinkle. */
const STAR_TICK_MS = 90;
/** A star grows then shrinks: dot, cross, big cross, cross, dot. */
const STAR_PHASES = [0, 1, 2, 1, 0] as const;

/** Star shapes, as rows of pixels: `#` lit, `o` the bright core. */
const STAR_SHAPES: readonly (readonly string[])[] = [
  ['o'],
  ['.#.', '#o#', '.#.'],
  ['..#..', '..#..', '##o##', '..#..', '..#..'],
];

export interface ShineOptions {
  scene: Phaser.Scene;
  /** The piece sprite drawn for a slot, to find where its pixels are. */
  pieceOf: (slot: VisibleSlot) => Phaser.GameObjects.Sprite | undefined;
  /** The golden ring's looping clip; without it, no crown. */
  crown?: { atlasKey: string; clip: string; firstFrame: string };
}

interface Star {
  sprite: Phaser.GameObjects.Image;
  slot: VisibleSlot;
  /** Index into STAR_PHASES, or -1 while waiting for the next twinkle. */
  phase: number;
  wait: number;
}

/** Opaque pixels of an atlas frame, cached: frames never change at runtime. */
const opaqueCache = new Map<string, readonly (readonly [number, number])[]>();

export function opaquePixels(frame: Phaser.Textures.Frame): readonly (readonly [number, number])[] {
  const key = `${frame.texture.key}:${frame.name}`;
  const cached = opaqueCache.get(key);
  if (cached !== undefined) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = frame.cutWidth;
  canvas.height = frame.cutHeight;
  const context = canvas.getContext('2d');
  const pixels: [number, number][] = [];
  if (context !== null) {
    context.drawImage(
      frame.source.image as CanvasImageSource,
      frame.cutX,
      frame.cutY,
      frame.cutWidth,
      frame.cutHeight,
      0,
      0,
      frame.cutWidth,
      frame.cutHeight,
    );
    const data = context.getImageData(0, 0, frame.cutWidth, frame.cutHeight).data;
    for (let y = 0; y < frame.cutHeight; y += 1) {
      for (let x = 0; x < frame.cutWidth; x += 1) {
        if ((data[(y * frame.cutWidth + x) * 4 + 3] ?? 0) > 0) pixels.push([x, y]);
      }
    }
  }
  opaqueCache.set(key, pixels);
  return pixels;
}

/**
 * A star's texture for a level and a shape, drawn once per scene: the arms in
 * the level's colour, the core white, so it reads as a glint on any piece.
 */
function starTexture(scene: Phaser.Scene, level: number, shape: number): string {
  const key = `fx.star.${String(level)}.${String(shape)}`;
  if (scene.textures.exists(key)) return key;
  const rows = STAR_SHAPES[shape] ?? ['o'];
  const color = upgradeColor(level) ?? 0xffffff;
  const graphics = scene.make.graphics({}, false);
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x += 1) {
      const pixel = row[x];
      if (pixel === '.') continue;
      graphics.fillStyle(pixel === 'o' ? 0xffffff : color, 1);
      graphics.fillRect(x, y, 1, 1);
    }
  });
  graphics.generateTexture(key, rows[0]?.length ?? 1, rows.length);
  graphics.destroy();
  return key;
}

/**
 * The halo of a level, drawn once per scene: a faint disc, a bright one-pixel
 * ring, and a dimmer ring just outside it, on an odd square so its centre is
 * a whole pixel.
 */
/** A bare one-pixel ring of a radius and colour, for the waves. */
function ringTexture(scene: Phaser.Scene, radius: number, color: number): string {
  const key = `fx.ring.${String(radius)}.${color.toString(16)}`;
  if (scene.textures.exists(key)) return key;
  const size = 2 * radius + 3;
  const centre = (size - 1) / 2;
  const graphics = scene.make.graphics({}, false);
  graphics.fillStyle(color, 1);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (Math.abs(Math.hypot(x - centre, y - centre) - radius) < 0.5)
        graphics.fillRect(x, y, 1, 1);
    }
  }
  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}

/** A single pixel of a colour, for the sparks' tails. */
function dotTexture(scene: Phaser.Scene, color: number): string {
  const key = `fx.dot.${color.toString(16)}`;
  if (scene.textures.exists(key)) return key;
  const graphics = scene.make.graphics({}, false);
  graphics.fillStyle(color, 1);
  graphics.fillRect(0, 0, 1, 1);
  graphics.generateTexture(key, 1, 1);
  graphics.destroy();
  return key;
}

/**
 * The inside of the burning halo, drawn once: a faint orange disc that warms
 * towards the ring, which is drawn live over it.
 */
function blazeDiscTexture(scene: Phaser.Scene, radius: number): string {
  const key = `fx.blaze-disc.${String(radius)}`;
  if (scene.textures.exists(key)) return key;
  const size = 2 * radius + 1;
  const graphics = scene.make.graphics({}, false);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const distance = Math.hypot(x - radius, y - radius);
      if (distance >= radius - 0.5) continue;
      const edge = radius - distance;
      const [color, alpha] =
        edge < 1.5 ? [BLAZE_RAMP[2], 0.45] : edge < 3 ? [BLAZE_RAMP[1], 0.28] : [BLAZE_RAMP[1], 0.14];
      graphics.fillStyle(color, alpha);
      graphics.fillRect(x, y, 1, 1);
    }
  }
  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}

function haloTexture(scene: Phaser.Scene, level: number, color: number): string {
  const key = `fx.halo.${String(level)}`;
  if (scene.textures.exists(key)) return key;
  const radius = HALO_BASE_RADIUS + level - 3;
  const size = 2 * (radius + 2) + 1;
  const centre = (size - 1) / 2;
  const graphics = scene.make.graphics({}, false);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const distance = Math.hypot(x - centre, y - centre);
      let alpha = 0;
      if (Math.abs(distance - radius) < 0.5) alpha = 0.85;
      else if (Math.abs(distance - (radius + 1)) < 0.5) alpha = 0.35;
      else if (distance < radius - 0.5) alpha = 0.12;
      if (alpha === 0) continue;
      graphics.fillStyle(color, alpha);
      graphics.fillRect(x, y, 1, 1);
    }
  }
  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}

export class UpgradeShine {
  /** Behind the character: the halo and the crown. */
  readonly back: Phaser.GameObjects.Container;
  /** In front of the character's layers, under its plate: the stars. */
  readonly front: Phaser.GameObjects.Container;
  private levels: Partial<Record<VisibleSlot, number>> = {};
  private aura = 0;
  private halo?: Phaser.GameObjects.Image;
  private haloPulse?: Phaser.Tweens.Tween;
  /** What moves with the halo: its sparks, their lap, its waves. */
  private haloLevel = 0;
  private sparks: Phaser.GameObjects.Image[][] = [];
  private lap?: Phaser.Tweens.Tween;
  private wave?: { image: Phaser.GameObjects.Image; step: number };
  private ticksToWave = 0;
  private crown?: Phaser.GameObjects.Sprite;
  /** At +10, the halo burns: its ring and flames redrawn every tick. */
  private blaze?: {
    graphics: Phaser.GameObjects.Graphics;
    ring: Pixel[];
    /** The ring's pixels with open air above them, where flames rise. */
    tops: Pixel[];
    embers: Ember[];
    ticks: number;
  };
  private stars: Star[] = [];
  private readonly timer: Phaser.Time.TimerEvent;

  constructor(private readonly options: ShineOptions) {
    const { scene } = options;
    this.back = scene.add.container(0, 0);
    this.front = scene.add.container(0, 0);
    this.timer = scene.time.addEvent({
      delay: STAR_TICK_MS,
      loop: true,
      callback: () => {
        this.tick();
      },
    });
  }

  /** The level of each visible piece and the outfit's aura; redrawn only what changes. */
  set(levels: Partial<Record<VisibleSlot, number>>, aura: number): void {
    if (aura !== this.aura) {
      this.aura = aura;
      this.setHalo(aura);
      this.setCrown(aura >= CROWN_AT_LEVEL);
    }
    if (VISIBLE_SLOTS.every((slot) => (levels[slot] ?? 0) === (this.levels[slot] ?? 0))) return;
    this.levels = { ...levels };
    this.setStars();
  }

  setVisible(visible: boolean): void {
    this.back.setVisible(visible);
    this.front.setVisible(visible);
  }

  destroy(): void {
    this.timer.remove(false);
    this.haloPulse?.remove();
    this.lap?.remove();
    this.back.destroy(true);
    this.front.destroy(true);
  }

  private setHalo(level: number): void {
    this.haloPulse?.remove();
    this.haloPulse = undefined;
    this.halo?.destroy();
    this.halo = undefined;
    this.lap?.remove();
    this.lap = undefined;
    for (const spark of this.sparks) for (const image of spark) image.destroy();
    this.sparks = [];
    this.wave?.image.destroy();
    this.wave = undefined;
    this.blaze?.graphics.destroy();
    this.blaze = undefined;
    this.haloLevel = level;
    const color = upgradeColor(level);
    if (color === undefined) return;
    const intensity = intensityOf(level);
    const { scene } = this.options;
    this.ticksToWave = intensity.waveEvery;
    if (level >= CROWN_AT_LEVEL) {
      this.setBlaze(level);
      // The sparks stay: fire on a ring that also runs is what makes +10 loud.
      this.addSparks(level, BLAZE_RAMP[1], intensity);
      return;
    }
    const key = haloTexture(scene, level, color);
    const size = scene.textures.getFrame(key).width;
    this.halo = scene.add.image(0, HALO_CENTRE_Y, key);
    // Centre on a whole pixel: the texture is odd-sized, so its middle is one.
    this.halo.setDisplayOrigin((size - 1) / 2, (size - 1) / 2);
    this.back.addAt(this.halo, 0);
    this.haloPulse = scene.tweens.add({
      targets: this.halo,
      alpha: { from: 1, to: intensity.pulseLow },
      duration: intensity.pulseMs / 2,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.addSparks(level, color, intensity);
  }

  /** The burning halo: a disc that breathes, and a Graphics for the ring, redrawn by `stepBlaze`. */
  private setBlaze(level: number): void {
    const { scene } = this.options;
    const radius = HALO_BASE_RADIUS + level - 3;
    const disc = blazeDiscTexture(scene, radius);
    this.halo = scene.add.image(0, HALO_CENTRE_Y, disc).setDisplayOrigin(radius, radius);
    this.back.addAt(this.halo, 0);
    const intensity = intensityOf(level);
    this.haloPulse = scene.tweens.add({
      targets: this.halo,
      alpha: { from: 1, to: intensity.pulseLow },
      duration: intensity.pulseMs / 2,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    const graphics = scene.add.graphics();
    this.back.addAt(graphics, 1);
    const ring = circlePixels(radius).map(([x, y]) => [x, y + HALO_CENTRE_Y] as const);
    const tops = ring.filter(
      ([x, y]) => Math.hypot(x, y - 1 - HALO_CENTRE_Y) > radius + 0.5 && y < HALO_CENTRE_Y,
    );
    this.blaze = { graphics, ring, tops, embers: [], ticks: 0 };
  }

  /**
   * One tick of the burning halo, as a legendary outline burns: every pixel of
   * the ring lit, its shade from its angle and the tick so bright arcs run
   * round; a dim ember ring just outside; flames from the top half, hottest at
   * their root; and embers flying up off it, swaying.
   */
  private stepBlaze(): void {
    const blaze = this.blaze;
    if (blaze === undefined) return;
    blaze.ticks += 1;
    const { graphics, ring, tops } = blaze;
    graphics.clear();
    const turn = (2 * Math.PI * blaze.ticks) / BLAZE_LAP;
    for (const [x, y] of ring) {
      const angle = Math.atan2(y - HALO_CENTRE_Y, x);
      const wave = (1 + Math.cos(BLAZE_ARCS * angle - turn)) / 2;
      // Never below the rarity's orange: on a ring this big, dark pixels read as dull.
      graphics.fillStyle(BLAZE_RAMP[Math.min(4, 2 + Math.floor(wave * 3))] ?? BLAZE_RAMP[2], 1);
      graphics.fillRect(x, y, 1, 1);
      // Two rings of glow outside it, orange then embers.
      const out = Math.hypot(x, y - HALO_CENTRE_Y);
      for (const [far, color, alpha] of [
        [1, BLAZE_RAMP[1], 0.55 + 0.35 * wave],
        [2, BLAZE_RAMP[0], 0.3],
      ] as const) {
        graphics.fillStyle(color, alpha);
        graphics.fillRect(
          Math.round(x + (far * x) / out),
          Math.round(y + (far * (y - HALO_CENTRE_Y)) / out),
          1,
          1,
        );
      }
    }
    const top = BLAZE_RAMP.length - 1;
    for (const [x, y] of tops) {
      // Flicker from a hash of the column and the tick: steady for a tick, never in step.
      const roll = hash(x, y, blaze.ticks);
      if (roll >= FLAME_CHANCE) continue;
      const height = 1 + Math.floor((roll / FLAME_CHANCE) * FLAME_HEIGHT);
      for (let step = 1; step <= height; step += 1) {
        const heat = BLAZE_RAMP[Math.max(0, top - step)] ?? BLAZE_RAMP[0];
        graphics.fillStyle(heat, 1 - (step - 1) / (height + 1));
        graphics.fillRect(x, y - step, 1, 1);
      }
    }
    if (Math.random() < EMBER_CHANCE) {
      const from = tops[Math.floor(Math.random() * tops.length)];
      if (from !== undefined) {
        blaze.embers.push({
          x: from[0],
          y: from[1] - 1,
          age: 0,
          life: 8 + Math.floor(Math.random() * 6),
          color: BLAZE_RAMP[2 + Math.floor(Math.random() * 3)] ?? BLAZE_RAMP[2],
          sway: Math.random() < 0.5 ? -1 : 1,
        });
      }
    }
    blaze.embers = blaze.embers.filter((ember) => {
      ember.age += 1;
      if (ember.age > ember.life) return false;
      ember.y -= 1;
      if (ember.age % 3 === 0) ember.sway = -ember.sway;
      if (ember.age % 2 === 0) ember.x += ember.sway;
      const alpha = ember.age < ember.life / 2 ? 1 : 2 * (1 - ember.age / ember.life);
      graphics.fillStyle(ember.color, Math.max(0.15, alpha));
      graphics.fillRect(ember.x, ember.y, 1, 1);
      return true;
    });
  }

  /**
   * Sparks running round the ring, evenly spaced: a white-cored cross with a
   * two-pixel tail in the level's colour. Placed on whole pixels of the ring
   * at every frame of the lap.
   */
  private addSparks(level: number, color: number, intensity: HaloIntensity): void {
    if (intensity.sparks === 0) return;
    const { scene } = this.options;
    const radius = HALO_BASE_RADIUS + level - 3;
    for (let k = 0; k < intensity.sparks; k += 1) {
      const head = scene.add.image(0, 0, starTexture(scene, level, 1));
      head.setDisplayOrigin(1, 1);
      const tail = [0.7, 0.4].map((alpha) =>
        scene.add.image(0, 0, dotTexture(scene, color)).setOrigin(0, 0).setAlpha(alpha),
      );
      const spark = [head, ...tail];
      for (const image of spark) this.back.add(image);
      this.sparks.push(spark);
    }
    const place = (turn: number): void => {
      this.sparks.forEach((spark, k) => {
        const start = turn + (k / this.sparks.length) * Math.PI * 2;
        spark.forEach((image, dot) => {
          const angle = start - dot * SPARK_TAIL_STEP;
          image.setPosition(
            Math.round(Math.cos(angle) * radius),
            Math.round(HALO_CENTRE_Y + Math.sin(angle) * radius),
          );
        });
      });
    };
    place(0);
    this.lap = scene.tweens.addCounter({
      from: 0,
      to: Math.PI * 2,
      duration: intensity.lapMs,
      repeat: -1,
      onUpdate: (tween) => {
        place(tween.getValue() ?? 0);
      },
    });
  }

  /** From +8: now and then a ring rolls out of the halo, a pixel a tick, fading. */
  private stepWave(): void {
    const every = intensityOf(this.haloLevel).waveEvery;
    const color = this.blaze !== undefined ? BLAZE_RAMP[1] : upgradeColor(this.haloLevel);
    if (every === 0 || color === undefined || this.halo === undefined) return;
    const { scene } = this.options;
    const radius = HALO_BASE_RADIUS + this.haloLevel - 3;
    if (this.wave !== undefined) {
      this.wave.step += 1;
      if (this.wave.step > WAVE_STEPS) {
        this.wave.image.destroy();
        this.wave = undefined;
        return;
      }
      const key = ringTexture(scene, radius + 1 + this.wave.step, color);
      const size = scene.textures.getFrame(key).width;
      this.wave.image.setTexture(key).setDisplayOrigin((size - 1) / 2, (size - 1) / 2);
      this.wave.image.setAlpha(0.8 * (1 - this.wave.step / (WAVE_STEPS + 1)));
      return;
    }
    this.ticksToWave -= 1;
    if (this.ticksToWave > 0) return;
    this.ticksToWave = every;
    const key = ringTexture(scene, radius + 1, color);
    const size = scene.textures.getFrame(key).width;
    const image = scene.add.image(0, HALO_CENTRE_Y, key).setAlpha(0.8);
    image.setDisplayOrigin((size - 1) / 2, (size - 1) / 2);
    this.back.add(image);
    this.wave = { image, step: 0 };
  }

  private setCrown(wanted: boolean): void {
    const crown = this.options.crown;
    if (wanted && this.crown === undefined && crown !== undefined) {
      const { scene } = this.options;
      this.crown = scene.add.sprite(0, HALO_CENTRE_Y, crown.atlasKey, crown.firstFrame);
      this.crown.setOrigin(0.5, 0.5);
      if (scene.anims.exists(crown.clip)) this.crown.play(crown.clip);
      this.back.add(this.crown);
    } else if (!wanted && this.crown !== undefined) {
      this.crown.destroy();
      this.crown = undefined;
    }
  }

  /** As many stars per piece as its level earns, each starting at a random moment. */
  private setStars(): void {
    for (const star of this.stars) star.sprite.destroy();
    this.stars = [];
    for (const slot of VISIBLE_SLOTS) {
      const level = this.levels[slot] ?? 0;
      for (let k = 0; k < upgradeStarCount(level); k += 1) {
        const sprite = this.options.scene.add
          .image(0, 0, starTexture(this.options.scene, level, 0))
          .setVisible(false);
        this.front.add(sprite);
        this.stars.push({ sprite, slot, phase: -1, wait: Math.floor(Math.random() * 12) });
      }
    }
  }

  /** One step of every star: wait, land on a pixel of its piece, twinkle, vanish. */
  private tick(): void {
    this.stepBlaze();
    this.stepWave();
    for (const star of this.stars) {
      if (star.phase < 0) {
        if (star.wait > 0) {
          star.wait -= 1;
          continue;
        }
        if (!this.land(star)) {
          star.wait = 4;
          continue;
        }
        star.phase = 0;
      }
      const shape = STAR_PHASES[star.phase];
      if (shape === undefined) {
        star.sprite.setVisible(false);
        star.phase = -1;
        // Higher levels twinkle more often.
        const level = this.levels[star.slot] ?? 0;
        star.wait = Math.max(1, 12 - level) + Math.floor(Math.random() * 6);
        continue;
      }
      const size = STAR_SHAPES[shape]?.length ?? 1;
      star.sprite.setTexture(starTexture(this.options.scene, this.levels[star.slot] ?? 0, shape));
      star.sprite.setDisplayOrigin((size - 1) / 2, (size - 1) / 2);
      star.sprite.setVisible(true);
      star.phase += 1;
    }
  }

  /** Put a star on a random opaque pixel of its piece, as the piece is drawn right now. */
  private land(star: Star): boolean {
    const piece = this.options.pieceOf(star.slot);
    if (piece?.visible !== true) return false;
    const pixels = opaquePixels(piece.frame);
    const pixel = pixels[Math.floor(Math.random() * pixels.length)];
    if (pixel === undefined) return false;
    star.sprite.setPosition(
      Math.round(piece.x - piece.displayOriginX + pixel[0]),
      Math.round(piece.y - piece.displayOriginY + pixel[1]),
    );
    return true;
  }
}
